import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PrinterIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Field, getApiErrorMessage, inputClass, primaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';

type Kind = 'product' | 'batch' | 'serial' | 'tool' | 'instrument' | 'machine' | 'warehouse';
type Format = 'a4-24' | 'thermal-100x50' | 'thermal-50x25';
type Symbology = 'both' | 'code128' | 'qr';

interface Candidate {
  id: string;
  code: string;
  title: string;
  detail: string;
}

interface Chosen extends Candidate {
  copies: number;
}

const KINDS: { key: Kind; label: string; permission: string }[] = [
  { key: 'product', label: 'Items', permission: 'inventory.read' },
  { key: 'batch', label: 'Batches', permission: 'inventory.read' },
  { key: 'serial', label: 'Serial numbers', permission: 'inventory.read' },
  { key: 'warehouse', label: 'Warehouses', permission: 'inventory.read' },
  { key: 'tool', label: 'Tools', permission: 'manufacturing.read' },
  { key: 'machine', label: 'Machines', permission: 'manufacturing.read' },
  { key: 'instrument', label: 'Instruments', permission: 'quality.read' },
];

const FORMATS: { key: Format; label: string }[] = [
  { key: 'a4-24', label: 'A4 sheet, 24 labels of 70 × 37 mm' },
  { key: 'thermal-100x50', label: 'Thermal label 100 × 50 mm' },
  { key: 'thermal-50x25', label: 'Thermal label 50 × 25 mm' },
];

const SYMBOLOGIES: { key: Symbology; label: string }[] = [
  { key: 'both', label: 'Bar code and QR code' },
  { key: 'code128', label: 'Bar code only (Code 128)' },
  { key: 'qr', label: 'QR code only' },
];

const MAX_LABELS = 1000;

// A failed download comes back as a blob; its message is inside
const blobMessage = async (error: unknown, fallback: string): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return getApiErrorMessage(error, fallback);
};

const Labels: React.FC = () => {
  const { can } = usePermissions();
  const kinds = KINDS.filter((kind) => can(kind.permission));
  const [params] = useSearchParams();
  const askedKind = params.get('kind') as Kind | null;
  const [kind, setKind] = useState<Kind>(askedKind && kinds.some((item) => item.key === askedKind) ? askedKind : (kinds[0]?.key ?? 'product'));
  const [searchInput, setSearchInput] = useState(params.get('search') ?? '');
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [chosen, setChosen] = useState<Chosen[]>([]);
  const [format, setFormat] = useState<Format>('a4-24');
  const [symbology, setSymbology] = useState<Symbology>('both');
  const [skip, setSkip] = useState('0');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: candidates = [], isLoading } = useQuery<Candidate[]>({
    queryKey: ['label-candidates', kind, search],
    queryFn: async () => (await api.get('/labels/candidates', { params: { kind, ...(search ? { search } : {}) } })).data,
    enabled: kinds.length > 0,
  });

  const total = chosen.reduce((sum, item) => sum + item.copies, 0);
  const skipNumber = Number(skip);
  const skipValid = format !== 'a4-24' || (Number.isInteger(skipNumber) && skipNumber >= 0 && skipNumber <= 23);
  const sheets = format === 'a4-24' ? Math.ceil((total + (skipValid ? skipNumber : 0)) / 24) : total;

  const print = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        '/labels/print',
        { kind, items: chosen.map((item) => ({ id: item.id, copies: item.copies })), format, symbology, ...(format === 'a4-24' ? { skip: skipNumber } : {}) },
        { responseType: 'blob' },
      );
      const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = named?.[1] ?? 'Labels.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success(`${total} label${total === 1 ? '' : 's'} ready to print`),
    onError: async (error) => toast.error(await blobMessage(error, 'The labels could not be made'), { duration: 8000 }),
  });

  const changeKind = (value: Kind) => {
    setKind(value);
    setChosen([]);
    setSearchInput('');
  };
  const add = (candidate: Candidate) => setChosen((previous) => (previous.some((item) => item.id === candidate.id) ? previous : [...previous, { ...candidate, copies: 1 }]));
  const setCopies = (id: string, value: string) => {
    const copies = Math.max(1, Math.min(100, Math.floor(Number(value) || 1)));
    setChosen((previous) => previous.map((item) => (item.id === id ? { ...item, copies } : item)));
  };

  if (kinds.length === 0) {
    return <p className="py-10 text-center text-sm text-gray-500">Your role does not allow printing labels for anything.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Print Labels</h1>
        <p className="mt-1 text-sm text-gray-500">Labels with a bar code and a QR code that the Scan page reads back: for items, batches, serial numbers, tools, machines, instruments and warehouses.</p>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-gray-200" role="tablist">
        {kinds.map((item) => (
          <button key={item.key} type="button" role="tab" aria-selected={kind === item.key} onClick={() => changeKind(item.key)} className={cn('-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium', kind === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
          <div className="border-b border-gray-200 p-4">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by code or name" aria-label="Search what to print" className={cn(inputClass, 'pl-9')} />
            </div>
          </div>
          {isLoading ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">Loading…</p>
          ) : candidates.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">{search ? 'Nothing matches this search.' : 'There is nothing of this kind yet.'}</p>
          ) : (
            <ul className="max-h-[28rem] divide-y divide-gray-100 overflow-y-auto">
              {candidates.map((candidate) => {
                const added = chosen.some((item) => item.id === candidate.id);
                return (
                  <li key={candidate.id} className="flex items-center justify-between gap-3 px-4 py-2" data-testid="label-candidate">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium text-gray-900">{candidate.code}</p>
                      <p className="truncate text-xs text-gray-500">{candidate.title}{candidate.detail ? ` · ${candidate.detail}` : ''}</p>
                    </div>
                    <button type="button" onClick={() => add(candidate)} disabled={added} className={cn('rounded-md px-2.5 py-1 text-xs font-semibold ring-1 ring-inset', added ? 'text-gray-400 ring-gray-200' : 'text-indigo-600 ring-indigo-200 hover:bg-indigo-50')}>
                      {added ? 'Added' : 'Add'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {candidates.length === 50 && <p className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500">The first 50 are shown. Search to find others.</p>}
        </section>

        <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">To print</h2>
          {chosen.length === 0 ? (
            <p className="text-sm text-gray-500">Add what to print from the list.</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md ring-1 ring-gray-200">
              {chosen.map((item) => (
                <li key={item.id} className="flex items-center gap-3 px-3 py-2" data-testid="label-chosen">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-sm font-medium text-gray-900">{item.code}</p>
                    <p className="truncate text-xs text-gray-500">{item.title}</p>
                  </div>
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    Copies
                    <input type="number" min={1} max={100} value={item.copies} onChange={(event) => setCopies(item.id, event.target.value)} aria-label={`Copies of ${item.code}`} className={cn(inputClass, 'w-20 text-right')} />
                  </label>
                  <button type="button" onClick={() => setChosen((previous) => previous.filter((other) => other.id !== item.id))} aria-label={`Remove ${item.code}`} className="text-gray-400 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Label" htmlFor="label-format">
              <select id="label-format" value={format} onChange={(event) => setFormat(event.target.value as Format)} className={inputClass}>
                {FORMATS.map((item) => (<option key={item.key} value={item.key}>{item.label}</option>))}
              </select>
            </Field>
            <Field label="Codes" htmlFor="label-symbology">
              <select id="label-symbology" value={symbology} onChange={(event) => setSymbology(event.target.value as Symbology)} className={inputClass}>
                {SYMBOLOGIES.map((item) => (<option key={item.key} value={item.key}>{item.label}</option>))}
              </select>
            </Field>
            {format === 'a4-24' && (
              <Field label="Labels already used on the first sheet" htmlFor="label-skip" error={skipValid ? undefined : 'From 0 to 23'} hint="Printing starts after them, row by row">
                <input id="label-skip" type="number" min={0} max={23} value={skip} onChange={(event) => setSkip(event.target.value)} className={inputClass} />
              </Field>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600" data-testid="label-total">
              {total} label{total === 1 ? '' : 's'}
              {total > 0 && ` on ${sheets} ${format === 'a4-24' ? (sheets === 1 ? 'sheet' : 'sheets') : sheets === 1 ? 'thermal label' : 'thermal labels'}`}
              {total > MAX_LABELS && <span className="ml-2 text-red-600">At most {MAX_LABELS} at a time</span>}
            </p>
            <button type="button" onClick={() => print.mutate()} disabled={print.isPending || total === 0 || total > MAX_LABELS || !skipValid} className={primaryButtonClass}>
              <PrinterIcon className="-ml-0.5 mr-2 h-4 w-4" />
              {print.isPending ? 'Making the PDF…' : 'Download PDF'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Labels;
